import '../../utils/webgpuPolyfill.js'

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'

const MIN_DPR = 1.0
const MAX_DPR = 1.0
// Desktop and tablet tiers may render up to 2× DPR (crisp on hidpi/retina
// monitors); mobile tiers keep the 1.0 cap for GPU headroom.
const MAX_DPR_BY_BREAKPOINT = {
  desktop1080: 2.0,
  desktop4k: 2.0,
  desktop1440: 2.0,
  tabletLandscape: 2.0,
  tabletPortrait: 2.0,
}
const getClampedDPR = (mediaQueryKey) => {
  const maxDpr = MAX_DPR_BY_BREAKPOINT[mediaQueryKey] ?? MAX_DPR
  return Math.max(MIN_DPR, Math.min(window.devicePixelRatio || 1, maxDpr))
}
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { useIframeStore } from '../../store/iframeStore'
import WebGPU from 'three/examples/jsm/capabilities/WebGPU.js'
import * as THREE from 'three/webgpu'
import { WebGLRenderer, PCFSoftShadowMap } from 'three'
import PreloadManager from '../PreloadManager'
import { SceneReadyProvider } from '../SceneReadyManager'

// Import WebGPURenderer from the THREE.webgpu namespace
const { WebGPURenderer } = THREE

// Component to display renderer info in top right (hidden)
function RendererInfo({ rendererType }) {
  return null
}

// Pauses the render loop while an iframe 3D scene is active OR the crew profile
// (full-screen 2D overlay) is open — both fully obscure the 3D scene. It then
// waits for 2 rendered frames before un-hiding markers so Drei <Html> transforms
// are current before the markers become visible again.
//
// NOTE: the previous idle-pause (IdleDetector, tablet-portrait-only, 10 s of no
// interaction → frameloop 'demand') was REMOVED — it paused the render loop as a
// side-effect of device orientation (rotating to the tablet-portrait rotate-prompt
// armed the idle timer), and rotating back could leave the loop paused / not
// recovering. No render pause is tied to orientation any more.
function FrameLoopController() {
  const showIframe    = useIframeStore(state => state.showIframe)
  const isCrewProfileOpen = useIframeStore(state => state.isCrewProfileOpen)
  const setMarkersVisible = useIframeStore(state => state.setMarkersVisible)
  const { set, get } = useThree()
  const resumingRef   = useRef(false)
  const frameCountRef = useRef(0)

  // Derived: should the loop be paused for any reason?
  const shouldPause = showIframe || isCrewProfileOpen

  useLayoutEffect(() => {
    if (shouldPause) {
      setMarkersVisible(false)
      set({ frameloop: 'demand' })
    } else {
      // Reset the R3F clock so the first resumed frame doesn't get a huge delta
      // (accumulated during the demand pause) which would cause Earth to jump rotation.
      get().clock.getDelta()
      // Resume loop — markers stay hidden until useFrame confirms 2 frames rendered
      resumingRef.current = true
      frameCountRef.current = 0
      set({ frameloop: 'always' })
    }
  }, [shouldPause, set, setMarkersVisible, get])

  useFrame(() => {
    if (resumingRef.current) {
      frameCountRef.current += 1
      if (frameCountRef.current >= 2) {
        resumingRef.current = false
        setMarkersVisible(true)
      }
    }
  })

  return null
}

export const WebGPUCanvas = ({ children, camera, onPreloadProgress, onSceneReady, mediaQueryKey }) => {
  const [rendererType, setRendererType] = useState(
    WebGPU.isAvailable() ? 'WebGPU' : 'WebGL 2.0'
  )
  const isWebGPUAvailable = WebGPU.isAvailable()

  return (
    <>
      <RendererInfo rendererType={rendererType} />
      <Canvas
        camera={camera}
        performance={{ min: 0.5, debounce: 300 }}
        shadows
        frameloop="always"
        resize={{ debounce: 100 }}
        // Clamp DPR through R3F itself. Previously R3F defaulted dpr to the raw
        // window.devicePixelRatio (2–3 on mobile) while a separate
        // WebGPUResizeHandler re-set it to getClampedDPR() on every resize —
        // two conflicting setPixelRatio+setSize calls per resize. On desktop (DPR 1)
        // they agreed and it was harmless; on a mobile/tablet resize the drawing
        // buffer was resized to two different sizes in the same commit, destroying a
        // render-target texture twice and, on WebGPU, mid-flight → "[Buffer] used in
        // submit while destroyed", killing the renderer. Letting R3F own DPR (clamped
        // here) makes setSize happen exactly once per resize with one consistent DPR.
        // getClampedDPR() applies the per-breakpoint clamp (2.0 on desktop/tablet,
        // 1.0 on mobile).
        dpr={Math.max(MIN_DPR, getClampedDPR(mediaQueryKey))}
        gl={async (props) => {
          if (isWebGPUAvailable) {
            try {
              const renderer = new WebGPURenderer({
                ...props,
                forceWebGL: false,
              })
              await renderer.init()

              // Explicitly enable shadow maps — WebGPURenderer does not pick up
              // R3F's <Canvas shadows> prop automatically the way WebGLRenderer does.
              renderer.shadowMap.enabled = true
              renderer.shadowMap.type    = PCFSoftShadowMap
              
              
              // Set initial size from canvas element to prevent first-frame mismatch
              if (props.canvas) {
                const width = props.canvas.clientWidth
                const height = props.canvas.clientHeight
                renderer.setPixelRatio(getClampedDPR(mediaQueryKey))
                renderer.setSize(width, height, false)
              }
              
              setRendererType('WebGPU')
              return renderer
            } catch (err) {
              if (import.meta.env.DEV) console.warn('WebGPU initialization failed, falling back to WebGL:', err)
              setRendererType('WebGL 2.0 (fallback)')
              return new WebGLRenderer({
                ...props,
                antialias: true,
              })
            }
          } else {
            setRendererType('WebGL 2.0')
            return new WebGLRenderer({
              ...props,
              antialias: true,
            })
          }
        }}
      >
        <color attach="background" args={['#000000']} />
        <FrameLoopController />
        <PreloadManager onProgress={onPreloadProgress}>
          <SceneReadyProvider onAllReady={onSceneReady}>
            {children}
          </SceneReadyProvider>
        </PreloadManager>
      </Canvas>
    </>
  )
}

export default WebGPUCanvas
